/**
 * Error Handling Utilities
 * Provides consistent error handling and normalization across the calendar component
 */

/**
 * Normalizes error objects to a consistent format
 * @param {Error|string|object} error - The error to normalize
 * @param {string} defaultMessage - Default message if error is invalid
 * @returns {object} Normalized error object with { message, type, originalError, status }
 */
export const normalizeError = (error, defaultMessage = 'An unexpected error occurred') => {
    if (!error) {
        return {
            message: defaultMessage,
            type: 'unknown',
            originalError: null,
            status: null,
        };
    }

    // If it's already a normalized error object, return it
    if (error && typeof error === 'object' && error.message && error.type) {
        return error;
    }

    // Handle axios errors (most common in React Query)
    if (error && typeof error === 'object' && error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error?.message || error.message || defaultMessage;
        
        let type = 'error';
        if (status === 401) {
            type = 'unauthorized';
        } else if (status === 403) {
            type = 'forbidden';
        } else if (status === 404) {
            type = 'notFound';
        } else if (status === 408) {
            type = 'timeout';
        } else if (status === 429) {
            type = 'rateLimit';
        } else if (status >= 500) {
            type = 'server';
        } else if (status >= 400) {
            type = 'validation';
        }

        return {
            message,
            type,
            originalError: error,
            status,
        };
    }

    // Handle network errors (no response)
    if (error && typeof error === 'object' && error.request && !error.response) {
        return {
            message: 'Network error. Please check your connection.',
            type: 'network',
            originalError: error,
            status: null,
        };
    }

    // If it's an Error object
    if (error instanceof Error) {
        let type = 'error';
        if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
            type = 'timeout';
        } else if (error.message?.includes('network') || error.message?.includes('Network')) {
            type = 'network';
        }

        return {
            message: error.message || defaultMessage,
            type: type,
            originalError: error,
            status: null,
        };
    }

    // If it's a string
    if (typeof error === 'string') {
        return {
            message: error,
            type: 'error',
            originalError: error,
            status: null,
        };
    }

    // If it's an object with a message property
    if (error && typeof error === 'object' && error.message) {
        return {
            message: error.message,
            type: error.type || 'error',
            originalError: error,
            status: error.status || null,
        };
    }

    // Fallback
    return {
        message: String(error) || defaultMessage,
        type: 'error',
        originalError: error,
        status: null,
    };
};

/**
 * Gets a user-friendly, actionable error message based on error type
 * @param {object} normalizedError - Normalized error object
 * @param {string} context - Context of the error (e.g., 'loading events', 'updating event')
 * @returns {object} Object with message and action
 */
export const getErrorMessage = (normalizedError, context = '') => {
    const { message, type, status, originalError } = normalizedError;
    const contextText = context ? ` while ${context}` : '';

    // Map common error types to user-friendly, actionable messages
    const errorMessages = {
        network: {
            message: `Unable to connect to the server${contextText}.`,
            action: 'Please check your internet connection and try again.',
            actionable: true,
        },
        timeout: {
            message: `The request took too long${contextText}.`,
            action: 'Please try again. If the problem persists, check your internet connection.',
            actionable: true,
        },
        unauthorized: {
            message: `Your session has expired${contextText}.`,
            action: 'Please refresh the page to sign in again.',
            actionable: true,
        },
        forbidden: {
            message: `You don't have permission to perform this action${contextText}.`,
            action: 'Please contact your administrator if you believe this is an error.',
            actionable: false,
        },
        notFound: {
            message: `The requested resource was not found${contextText}.`,
            action: 'It may have been deleted or moved. Please refresh the page.',
            actionable: true,
        },
        server: {
            message: `A server error occurred${contextText}.`,
            action: 'Please try again in a few moments. If the problem continues, contact support.',
            actionable: true,
        },
        validation: {
            message: `Invalid data provided${contextText}.`,
            action: 'Please check your input and try again.',
            actionable: true,
        },
        rateLimit: {
            message: `Too many requests${contextText}.`,
            action: 'Please wait a moment and try again.',
            actionable: true,
        },
    };

    const errorInfo = errorMessages[type] || {
        message: message || `An unexpected error occurred${contextText}.`,
        action: 'Please try again or contact support if the problem persists.',
        actionable: true,
    };

    // Enhance with specific HTTP status messages
    if (status === 401) {
        return {
            message: 'Your session has expired.',
            action: 'Please refresh the page to sign in again.',
            actionable: true,
        };
    }

    if (status === 403) {
        return {
            message: "You don't have permission to access this resource.",
            action: 'Please contact your administrator if you believe this is an error.',
            actionable: false,
        };
    }

    if (status === 404) {
        return {
            message: 'The requested resource was not found.',
            action: 'It may have been deleted or moved. Please refresh the page.',
            actionable: true,
        };
    }

    if (status === 429) {
        return {
            message: 'Too many requests. Please slow down.',
            action: 'Please wait a moment and try again.',
            actionable: true,
        };
    }

    if (status >= 500) {
        return {
            message: 'A server error occurred.',
            action: 'Please try again in a few moments. If the problem continues, contact support.',
            actionable: true,
        };
    }

    // Check for specific error messages from the API
    if (originalError?.response?.data?.message) {
        return {
            message: originalError.response.data.message,
            action: errorInfo.action,
            actionable: errorInfo.actionable,
        };
    }

    return errorInfo;
};

/**
 * Checks if an error is retryable
 * @param {object} normalizedError - Normalized error object
 * @returns {boolean} True if the error is retryable
 */
export const isRetryableError = (normalizedError) => {
    const retryableTypes = ['network', 'timeout', 'server', 'rateLimit'];
    return retryableTypes.includes(normalizedError.type);
};

/**
 * Creates a consistent error object from multiple error sources
 * @param {Array} errors - Array of error objects/strings
 * @returns {Array} Array of normalized error objects
 */
export const normalizeErrors = (errors) => {
    if (!Array.isArray(errors)) {
        return [];
    }
    return errors
        .filter(Boolean)
        .map((error) => normalizeError(error));
};

